import {
  BadgePurple,
  Button,
  H5,
  Icon,
  InputContainer,
  InputLabel,
  InputNumber,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import { useForm } from "@tanstack/react-form";
import type { ProductTemplateType } from "@sellar-io/api-v2/types";
import { getDisplayNameByKey, orderableItemValues } from "@sellar-io/common";
import { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { CustomContainerSizeDialog } from "./CustomContainerSizeDialog";
import { Alert } from "../../../../components/alert/Alert";
import { EmptyStateCard } from "../../../../components/emptyStateCard/EmptyStateCard";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { useProductTemplateCreate } from "../../../../hooks/api/v2/productTemplate/useProductTemplateCreate";
import { useProductTemplateDeleteMany } from "../../../../hooks/api/v2/productTemplate/useProductTemplateDeleteMany";
import {
  containerCategories,
  getContainerOptions,
  getContainerCategory,
  getContainerSizes,
} from "./productContainerTemplateOptions";
import type { ContainerCategory } from "./productContainerTemplateOptions";

type ContainerRow = {
  id: string;
  category: ContainerCategory;
  containerType: string;
  packQuantity: number | undefined;
  size: string;
  volume: number | undefined;
  displayUnit: string;
};

const isCompleteRow = (
  row: ContainerRow,
): row is ContainerRow & { volume: number; packQuantity: number } =>
  Boolean(row.containerType && row.size) &&
  row.volume !== undefined &&
  Number.isFinite(row.volume) &&
  row.volume > 0 &&
  row.packQuantity !== undefined &&
  Number.isInteger(row.packQuantity) &&
  row.packQuantity >= 1;

const newRow = (category: ContainerCategory): ContainerRow => ({
  id: crypto.randomUUID(),
  category,
  containerType: ["can", "bottle", "bagInBox"].includes(category)
    ? category
    : "",
  packQuantity: 1,
  size: category === "other" ? "custom" : "",
  volume: undefined,
  displayUnit: "L",
});

const templateMatchesRow = (template: ProductTemplateType, row: ContainerRow) =>
  template.containerType === row.containerType &&
  template.packQuantity === row.packQuantity &&
  template.volume === row.volume &&
  template.displayUnit === row.displayUnit;

const rowFromTemplate = (template: ProductTemplateType): ContainerRow => {
  const category = getContainerCategory(template.containerType);
  const sizeIndex = getContainerSizes(category).findIndex(
    (size) =>
      size.volume === template.volume &&
      size.displayUnit === template.displayUnit,
  );
  return {
    ...newRow(category),
    ...template,
    id: crypto.randomUUID(),
    size:
      category === "other" || sizeIndex === -1 ? "custom" : String(sizeIndex),
  };
};

export const ProductContainerTemplateForm = ({
  onFinished,
  onStarted,
  templates,
}: {
  onFinished: () => void;
  onStarted: () => void;
  templates: ProductTemplateType[];
}) => {
  const [step, setStep] = useState<"formats" | "details">("formats");
  const [initialRows] = useState(() => templates.map(rowFromTemplate));
  const [categories, setCategories] = useState<ContainerCategory[]>(() => [
    ...new Set(initialRows.map((row) => row.category)),
  ]);
  const savedTemplates = useRef(templates);
  const [customRow, setCustomRow] = useState<ContainerRow | null>(null);
  const [saving, setSaving] = useState(false);
  const createMutation = useProductTemplateCreate();
  const deleteMutation = useProductTemplateDeleteMany({ limit: 100 });
  const form = useForm({
    defaultValues: { rows: initialRows },
    onSubmit: async ({ value }) => {
      setSaving(true);
      try {
        const selectedRows = value.rows.filter((row) =>
          categories.includes(row.category),
        );
        if (!selectedRows.length || !selectedRows.every(isCompleteRow)) return;
        // Keep a saved baseline during partial failures so retries skip successful writes.
        for (const row of selectedRows) {
          if (
            savedTemplates.current.some((template) =>
              templateMatchesRow(template, row),
            )
          )
            continue;
          const template = await createMutation.mutateAsync({
            containerType: row.containerType,
            packQuantity: row.packQuantity,
            volume: row.volume,
            displayUnit: row.displayUnit,
          });
          savedTemplates.current = [...savedTemplates.current, template];
        }
        // Save replacements first so a failed creation cannot discard existing containers.
        const removedIds = savedTemplates.current
          .filter(
            (template) =>
              !selectedRows.some((row) => templateMatchesRow(template, row)),
          )
          .flatMap((template) =>
            template.id === undefined ? [] : [template.id],
          );
        if (removedIds.length)
          await deleteMutation.mutateAsync({ ids: removedIds });
        savedTemplates.current = savedTemplates.current.filter((template) =>
          selectedRows.some((row) => templateMatchesRow(template, row)),
        );
        onFinished();
      } catch {
        // The mutation error is displayed below; unsaved rows retain their values.
      } finally {
        setSaving(false);
      }
    },
  });
  const next = () => {
    onStarted();
    form.setFieldValue("rows", (rows) => [
      ...rows,
      ...categories
        .filter((category) => !rows.some((row) => row.category === category))
        .map(newRow),
    ]);
    setStep("details");
  };

  return (
    <EmptyStateCard
      icon="PACKAGE_VARIANT_CLOSED"
      badge={<BadgePurple icon="SELLAR_NEW">Product setup</BadgePurple>}
      title={
        step === "formats" ? "Add your containers" : "Add container details"
      }
      description={
        <p className="text-base">
          {step === "formats"
            ? "Tell us about the formats you sell your product in"
            : "Add a type and size for each format you selected"}
        </p>
      }
    >
      {step === "formats" ? (
        <div className="flex w-full flex-col gap-8">
          <div className="grid gap-4 sm:grid-cols-2">
            {containerCategories.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                aria-pressed={categories.includes(key)}
                className={`flex min-h-24 items-center justify-between rounded-lg border p-6 text-left font-semibold ${categories.includes(key) ? "border-zinc-800 bg-zinc-100" : "border-zinc-300 bg-white"}`}
                onClick={() =>
                  setCategories((selected) =>
                    selected.includes(key)
                      ? selected.filter((category) => category !== key)
                      : [...selected, key],
                  )
                }
              >
                {label}

                {categories.includes(key) && <Icon icon="CHECK_CIRCLE" />}
              </button>
            ))}
          </div>
          <div className="flex justify-end">
            <Button disabled={!categories.length} onClick={next}>
              Next
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="flex w-full flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!saving) void form.handleSubmit();
          }}
        >
          {(createMutation.isError || deleteMutation.isError) && (
            <Alert
              type="error"
              title="Unable to save your containers"
              message={
                apiV2AssertError(createMutation.error ?? deleteMutation.error)
                  ?.response?.data.message ??
                "Check the details and try again. Your saved containers have been kept."
              }
            />
          )}
          <fieldset disabled={saving} className="flex flex-col gap-4">
            <form.Field name="rows">
              {(field) => (
                <>
                  {containerCategories
                    .filter(
                      ({ key }) =>
                        categories.includes(key) &&
                        field.state.value.some((row) => row.category === key),
                    )
                    .map(({ key, label }) => (
                      <section
                        key={key}
                        className="flex flex-col gap-4 rounded-lg bg-zinc-50 p-4"
                      >
                        <H5>{label}</H5>
                        {field.state.value
                          .filter((row) => row.category === key)
                          .map((row) => {
                            const update = (patch: Partial<ContainerRow>) =>
                              field.handleChange(
                                field.state.value.map((current) =>
                                  current.id === row.id
                                    ? { ...current, ...patch }
                                    : current,
                                ),
                              );
                            const needsType = ![
                              "can",
                              "bottle",
                              "bagInBox",
                            ].includes(key);
                            const sizes = getContainerSizes(key);
                            const containerOptions = getContainerOptions(key);
                            return (
                              <div
                                key={row.id}
                                className={twMerge(
                                  "grid items-end gap-3",
                                  key === "other"
                                    ? "sm:grid-cols-[1fr_1fr_1fr_auto]"
                                    : "sm:grid-cols-[1fr_1fr_auto]",
                                )}
                              >
                                <div className="flex flex-col gap-2">
                                  <InputLabel>
                                    {needsType
                                      ? "Type"
                                      : key === "bagInBox"
                                        ? "Quantity"
                                        : "Pack size"}
                                  </InputLabel>
                                  <InputContainer>
                                    {needsType ? (
                                      <InputSelect
                                        id={`${row.id}-type`}
                                        required
                                        value={row.containerType}
                                        onChange={(event) =>
                                          update({
                                            containerType: event.target.value,
                                          })
                                        }
                                      >
                                        <InputOption value="">
                                          Select type
                                        </InputOption>
                                        {row.containerType &&
                                          !containerOptions.some(
                                            (option) =>
                                              option.key === row.containerType,
                                          ) && (
                                            <InputOption
                                              value={row.containerType}
                                            >
                                              {getDisplayNameByKey(
                                                orderableItemValues,
                                                row.containerType,
                                              )}
                                            </InputOption>
                                          )}
                                        {containerOptions.map((option) => (
                                          <InputOption
                                            key={option.key}
                                            value={option.key}
                                          >
                                            {option.display}
                                          </InputOption>
                                        ))}
                                      </InputSelect>
                                    ) : (
                                      <InputNumber
                                        id={`${row.id}-type`}
                                        required
                                        min={1}
                                        step={1}
                                        value={row.packQuantity ?? ""}
                                        onChange={(event) =>
                                          update({
                                            packQuantity:
                                              event.target.value === ""
                                                ? undefined
                                                : Number(event.target.value),
                                          })
                                        }
                                      />
                                    )}
                                  </InputContainer>
                                </div>
                                {key !== "other" && (
                                  <div className="flex flex-col gap-2">
                                    <InputLabel>Size</InputLabel>
                                    <InputContainer>
                                      <InputSelect
                                        id={`${row.id}-size`}
                                        required
                                        value={row.size}
                                        onChange={(event) => {
                                          if (event.target.value === "custom") {
                                            setCustomRow(row);
                                            return;
                                          }
                                          const size =
                                            sizes[Number(event.target.value)];
                                          update(
                                            size && event.target.value !== ""
                                              ? {
                                                  size: event.target.value,
                                                  volume: size.volume,
                                                  displayUnit: size.displayUnit,
                                                }
                                              : { size: "", volume: undefined },
                                          );
                                        }}
                                      >
                                        <InputOption value="">
                                          Select size
                                        </InputOption>
                                        {sizes.map((size, index) => (
                                          <InputOption
                                            key={`${size.volume}-${size.displayUnit}`}
                                            value={String(index)}
                                          >
                                            {size.volume}
                                            {size.displayUnit}
                                          </InputOption>
                                        ))}
                                        <InputOption value="custom">
                                          {row.size === "custom"
                                            ? `${row.volume}${row.displayUnit} (custom)`
                                            : "Custom size"}
                                        </InputOption>
                                      </InputSelect>
                                      {row.size === "custom" && (
                                        <Button
                                          type="button"
                                          color="transparent"
                                          onClick={() => setCustomRow(row)}
                                          aria-label="Edit custom size"
                                          className="h-5 w-5 shrink-0 p-0 leading-none"
                                        >
                                          <Icon icon="PENCIL" size="18px" />
                                        </Button>
                                      )}
                                    </InputContainer>
                                  </div>
                                )}
                                <Button
                                  type="button"
                                  color="transparent"
                                  className="order-last"
                                  aria-label={`Remove ${label} container`}
                                  onClick={() => {
                                    const remaining = field.state.value.filter(
                                      ({ id }) => id !== row.id,
                                    );
                                    if (
                                      !remaining.some(
                                        (current) => current.category === key,
                                      )
                                    )
                                      setCategories((selected) =>
                                        selected.filter(
                                          (category) => category !== key,
                                        ),
                                      );
                                    field.handleChange(
                                      field.state.value.filter(
                                        ({ id }) => id !== row.id,
                                      ),
                                    );
                                  }}
                                >
                                  <Icon icon="DELETE" />
                                </Button>
                                {key === "other" && (
                                  <>
                                    <div className="flex flex-col gap-2">
                                      <InputLabel>Volume</InputLabel>
                                      <InputContainer>
                                        <InputNumber
                                          id={`${row.id}-volume`}
                                          required
                                          min={0.01}
                                          step={0.01}
                                          value={row.volume ?? ""}
                                          onChange={(event) =>
                                            update({
                                              volume:
                                                event.target.value === ""
                                                  ? undefined
                                                  : Number(event.target.value),
                                            })
                                          }
                                        />
                                      </InputContainer>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                      <InputLabel>Unit</InputLabel>
                                      <InputContainer>
                                        <InputSelect
                                          id={`${row.id}-unit`}
                                          value={row.displayUnit}
                                          onChange={(event) =>
                                            update({
                                              displayUnit: event.target.value,
                                            })
                                          }
                                        >
                                          {[
                                            "L",
                                            "ml",
                                            "cl",
                                            "gal",
                                            "pt",
                                            "oz",
                                            "g",
                                            "kg",
                                          ].map((unit) => (
                                            <InputOption
                                              key={unit}
                                              value={unit}
                                            >
                                              {unit}
                                            </InputOption>
                                          ))}
                                        </InputSelect>
                                      </InputContainer>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        <div>
                          <Button
                            type="button"
                            color="transparent"
                            onClick={() =>
                              field.handleChange([
                                ...field.state.value,
                                newRow(key),
                              ])
                            }
                          >
                            + Add another
                          </Button>
                        </div>
                      </section>
                    ))}
                </>
              )}
            </form.Field>
          </fieldset>
          <div className="flex flex-wrap justify-between gap-3">
            <Button
              type="button"
              color="transparent"
              disabled={saving}
              onClick={() => setStep("formats")}
            >
              Back to container selection
            </Button>
            <form.Subscribe
              selector={(state) =>
                state.values.rows.filter((row) =>
                  categories.includes(row.category),
                )
              }
            >
              {(rows) => (
                <Button
                  type="submit"
                  loading={saving}
                  disabled={!rows.length || !rows.every(isCompleteRow)}
                >
                  Done
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      )}
      {customRow && (
        <CustomContainerSizeDialog
          volume={customRow.volume}
          displayUnit={customRow.displayUnit}
          onDismiss={() => setCustomRow(null)}
          onSave={(size) => {
            form.setFieldValue("rows", (rows) =>
              rows.map((row) =>
                row.id === customRow.id
                  ? { ...row, ...size, size: "custom" }
                  : row,
              ),
            );
            setCustomRow(null);
          }}
        />
      )}
    </EmptyStateCard>
  );
};
